import {
  CompleteStylistFragment,
  GetCheckedInStylistsDocument,
  useCheckInMutation,
  useGetStoreQuery,
  useGetStylistsLazyQuery,
} from 'drycut-graphql';
import { useEffect, useState } from 'react';
import { Button, Heading, Modal, Para, Stack } from 'ui-components';
import { StylistCheckInGrid } from './StylistCheckInGrid';
import { useAuth } from 'drycut-auth';
import { ChevronRightIcon } from '@heroicons/react/20/solid';

export function CheckInModal(): React.ReactElement {
  const { currentSlug } = useAuth();
  const [selectedStylist, setSelectedStylist] = useState<CompleteStylistFragment | null>(null);
  const [open, setOpen] = useState(false);
  const [checkIn, { loading: checkingIn }] = useCheckInMutation();

  const [getStylists, { loading, data }] = useGetStylistsLazyQuery({
    onCompleted: () => setOpen(true),
    fetchPolicy: 'network-only', // Always fetch the latest data from the server
  });

  const { data: storeData } = useGetStoreQuery({
    variables: { slug: currentSlug || '' },
  });

  // On button click fetch the available stylists and open the modal once the data is loaded
  const handleClick = async () => {
    getStylists({ variables: { onlyCheckedOut: true, onlyScheduled: true } });
  };

  const handleCheckIn = async () => {
    if (!selectedStylist) return;

    await checkIn({
      variables: {
        input: {
          stylistId: selectedStylist.id,
        },
      },
      refetchQueries: [GetCheckedInStylistsDocument],
    });

    setOpen(false);
  };

  // Unselect the stylist when the modal is closed
  useEffect(() => setSelectedStylist(null), [open]);

  const stylists = data?.stylists?.nodes || [];
  const filteredStylists = stylists.filter((stylist): stylist is CompleteStylistFragment =>
    Boolean(stylist),
  );

  const store = storeData?.store;

  const title = (
    <Stack>
      <Heading variant="h2" align="left">
        <Stack as="span" direction="row" className="items-center">
          {store?.name} <ChevronRightIcon className="w-12 h-12" /> Check in
        </Stack>
      </Heading>
      <Para align="left">Wähle dein Profil aus</Para>
    </Stack>
  );

  return (
    <>
      <Button color="primary" loading={loading} onClick={handleClick} className="w-48">
        Check in
      </Button>

      <Modal
        title={title}
        open={open}
        onClose={() => setOpen(false)}
        closeButton
        className="max-w-full w-10/12"
      >
        <StylistCheckInGrid
          storeSlug={currentSlug || ''}
          stylists={filteredStylists}
          onSelect={setSelectedStylist}
        />

        <Stack className="items-center mt-10">
          <Button
            className="w-fit inline-block"
            loading={checkingIn}
            disabled={!selectedStylist}
            onClick={handleCheckIn}
          >
            Check in @ {store?.name}
          </Button>
        </Stack>
      </Modal>
    </>
  );
}
